import type { Division, ReportMember } from "@gettactic/api";

export type LocationKind = "division" | "office" | "floor";

/** One row of a location tree; a node without children is what gets picked. */
export interface LocationNode {
  id: string;
  label: string;
  kind: LocationKind;
  children: LocationNode[];
}

type DivisionLike = Pick<Division, "id" | "name" | "parent_id">;

const node = (
  id: string,
  label: string,
  kind: LocationKind,
  children: LocationNode[] = []
): LocationNode => ({ id, label, kind, children });

/** Drops divisions with nothing to pick below them; divisions come before offices. */
const prune = (nodes: LocationNode[]): LocationNode[] => {
  const kept = nodes.flatMap((n): LocationNode[] => {
    if (n.kind !== "division") {
      return [n];
    }
    const children = prune(n.children);
    return children.length > 0 ? [{ ...n, children }] : [];
  });
  return [
    ...kept.filter((n) => n.kind === "division"),
    ...kept.filter((n) => n.kind !== "division")
  ];
};

/**
 * Divisions nested by parent, offices under their division (at the top when
 * they have none) and, when floors are given, floors under their office.
 * The deepest kind given is what gets picked: offices, or floors. Divisions
 * (and offices, once floors are the leaves) with nothing to pick below are
 * left out, so the tree only shows what the catalog lets the caller reach.
 * A floor whose office is unknown (a catalog that names no parents yet)
 * sits at the top, so it can still be picked.
 */
export const buildLocationTree = (
  divisions: DivisionLike[],
  offices: ReportMember[],
  floors?: ReportMember[]
): LocationNode[] => {
  const officeIds = new Set(offices.map((o) => o.id));
  const floorsOf = new Map<string, LocationNode[]>();
  const orphanFloors: LocationNode[] = [];
  for (const floor of floors ?? []) {
    const leaf = node(floor.id, floor.label, "floor");
    if (!floor.parent || !officeIds.has(floor.parent)) {
      orphanFloors.push(leaf);
      continue;
    }
    const list = floorsOf.get(floor.parent) ?? [];
    list.push(leaf);
    floorsOf.set(floor.parent, list);
  }
  const divisionNodes = new Map(
    divisions.map((d) => [d.id, node(d.id, d.name, "division")])
  );
  const roots: LocationNode[] = [];
  const attach = (child: LocationNode, parentId: string | null | undefined) => {
    const parent = parentId ? divisionNodes.get(parentId) : undefined;
    (parent ? parent.children : roots).push(child);
  };
  for (const division of divisions) {
    const own = divisionNodes.get(division.id);
    if (own) {
      attach(own, division.parent_id);
    }
  }
  for (const office of offices) {
    const officeFloors = floorsOf.get(office.id) ?? [];
    if (floors && officeFloors.length === 0) {
      continue;
    }
    attach(
      node(office.id, office.label, "office", officeFloors),
      office.parent
    );
  }
  roots.push(...orphanFloors);
  return prune(roots);
};

/** Every pickable id under a node: the node itself when it has no children. */
export const leafIds = (n: LocationNode): string[] =>
  n.children.length === 0 ? [n.id] : n.children.flatMap(leafIds);

/** Only the offices given, with what is under them, and the divisions holding them. */
export const keepOffices = (
  nodes: LocationNode[],
  officeIds: string[]
): LocationNode[] => {
  const keep = new Set(officeIds);
  const walk = (list: LocationNode[]): LocationNode[] =>
    list.flatMap((n): LocationNode[] => {
      if (n.kind === "office") {
        return keep.has(n.id) ? [n] : [];
      }
      if (n.kind === "division") {
        const children = walk(n.children);
        return children.length > 0 ? [{ ...n, children }] : [];
      }
      return [n];
    });
  return walk(nodes);
};

/** The one office a tree holds, when it holds nothing else; null otherwise. */
export const singleOffice = (nodes: LocationNode[]): LocationNode | null => {
  let current = nodes;
  while (current.length === 1) {
    const [only] = current;
    if (only.kind === "office") {
      return only;
    }
    if (only.kind !== "division") {
      return null;
    }
    current = only.children;
  }
  return null;
};

/**
 * A selection in words: the highest nodes wholly selected, so a division
 * stands for all its offices and a partial pick names the offices. A
 * division holding a single office is named by that office, which says
 * more. Ids the tree does not hold are named by `labelOf`, or by their id.
 */
export const summarizeSelection = (
  nodes: LocationNode[],
  selected: string[],
  labelOf: (id: string) => string = (id) => id
): string[] => {
  const set = new Set(selected);
  const known = new Set<string>();
  const out: string[] = [];
  const visit = (n: LocationNode) => {
    const leaves = leafIds(n);
    for (const id of leaves) {
      known.add(id);
    }
    const isLeaf = n.children.length === 0;
    if ((isLeaf || leaves.length > 1) && leaves.every((id) => set.has(id))) {
      out.push(n.label);
      return;
    }
    for (const child of n.children) {
      visit(child);
    }
  };
  for (const n of nodes) {
    visit(n);
  }
  for (const id of selected) {
    if (!known.has(id)) {
      out.push(labelOf(id));
    }
  }
  return out;
};
